import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, BarChart3, Building2, CalendarClock, CheckCircle2, CircleDollarSign, Handshake, LayoutDashboard, Menu, TrendingUp, X } from '../components/icons';
import { useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import Modal from '../components/Modal';
import ThemeToggle from '../components/ThemeToggle';

const promises = [
  'Mais reunioes com empresas qualificadas',
  'Processo comercial mais organizado e previsivel',
  'Apresentacao mais profissional para vender melhor'
];

const painPoints = [
  {
    title: 'Leads frios e sem contexto',
    text: 'Sua equipe perde tempo falando com contatos errados ou sem saber quem realmente decide.'
  },
  {
    title: 'Follow-up inconsistente',
    text: 'Oportunidades escapam porque o processo depende demais de memoria, planilhas e improviso.'
  },
  {
    title: 'Operacao com pouca credibilidade',
    text: 'Quando a apresentacao da solucao nao transmite confianca, a venda comeca mais fraca.'
  }
];

const solutions = [
  {
    icon: Building2,
    title: 'Leads e empresas em um so lugar',
    text: 'Organize contatos, empresas, etapas e historico comercial em uma interface clara e elegante.'
  },
  {
    icon: CalendarClock,
    title: 'Ritmo de execucao comercial',
    text: 'Crie uma rotina de prospeccao com prioridade, proximos passos e acompanhamento continuo.'
  },
  {
    icon: TrendingUp,
    title: 'Mais previsibilidade de receita',
    text: 'Visualize oportunidades, avance negociacoes e acompanhe o pipeline com leitura executiva.'
  }
];

const stats = [
  { value: '1.200+', label: 'empresas organizadas no pipeline' },
  { value: '31%', label: 'media de resposta em fluxos ativos' },
  { value: 'R$ 400 mil+', label: 'em oportunidades acompanhadas' }
];

const testimonials = [
  {
    name: 'Ana Ribeiro',
    role: 'Head of Sales',
    text: 'A pagina passou a vender muito melhor a proposta. Ficou com cara de produto serio e isso ajudou demais nas conversoes.'
  },
  {
    name: 'Diogo Mota',
    role: 'Revenue Ops',
    text: 'Agora a apresentacao conduz o cliente. Ela mostra valor, passa confianca e prepara melhor a conversa comercial.'
  }
];

const faqItems = [
  {
    question: 'Para quem o BIM Prospect faz mais sentido?',
    answer: 'Para empresas B2B que fazem prospeccao ativa, querem organizar leads e precisam vender com mais processo e mais credibilidade.'
  },
  {
    question: 'Consigo integrar com outras automacoes?',
    answer: 'Sim. A estrutura foi pensada para operar bem com fluxos conectados, inclusive automacoes em n8n.'
  },
  {
    question: 'A landing agora esta mais pensada para vendas?',
    answer: 'Sim. A nova pagina foi reorganizada com narrativa comercial, beneficios claros, prova social e chamadas de acao mais fortes.'
  }
];

const Presence = AnimatePresence as ComponentType<{ children?: ReactNode }>;

function ProductShowcase() {
  return (
    <div className="relative rounded-[1.75rem] border border-white/10 bg-slate-950 p-4 text-white shadow-[0_40px_100px_-40px_rgba(15,23,42,0.9)] sm:rounded-[2rem] sm:p-5">
      <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />

      <div className="flex flex-col gap-3 border-b border-white/10 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
            <LayoutDashboard className="h-5 w-5 text-emerald-300" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">Pipeline comercial</p>
            <p className="text-xs text-slate-400">Visao clara para SDRs, closers e lideranca</p>
          </div>
        </div>
        <span className="w-fit rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300">Ao vivo</span>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
            {([
              ['Pipeline ativo', '126 leads', BarChart3],
              ['Reunioes', '24 no mes', Handshake],
              ['Potencial', 'R$ 184 mil', CircleDollarSign]
            ] as const).map(([label, value, Icon]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between">
                  <p className="pr-3 text-[11px] uppercase tracking-[0.12em] text-slate-400 sm:text-xs sm:tracking-[0.16em]">{label}</p>
                  <Icon className="h-4 w-4 text-slate-500" />
                </div>
                <p className="mt-3 text-xl font-bold text-white">{value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-[1.75rem] border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">Progresso semanal</p>
                <p className="text-xs text-slate-400">Leitura rapida do desempenho comercial</p>
              </div>
              <TrendingUp className="h-5 w-5 text-emerald-300" />
            </div>

            <div className="mt-5 flex h-32 items-end gap-2">
              {[35, 54, 42, 61, 76, 70, 88].map((height, index) => (
                <div key={`${height}-${index}`} className="flex-1">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height }}
                    transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
                    className="rounded-t-2xl bg-gradient-to-t from-primary via-sky-400 to-emerald-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-[1.75rem] border border-white/10 bg-white/5 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">Oportunidades prioritarias</p>
              <p className="text-xs text-slate-400">Quem abordar e o que fazer agora</p>
            </div>
            <span className="w-fit rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-slate-200">Hoje</span>
          </div>

          <div className="mt-4 space-y-3">
            {[
              ['Nova Engenharia', 'Contato validado', 'R$ 48.000', 'Decisor confirmado'],
              ['Luso Digital', 'Retorno em andamento', 'R$ 21.500', 'Boa aderencia'],
              ['Pulse Foods', 'Proposta aberta', 'R$ 67.900', 'Janela de fechamento']
            ].map(([company, stage, value, note]) => (
              <div key={company} className="rounded-2xl border border-white/10 bg-slate-900/80 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-white">{company}</p>
                    <p className="mt-1 text-xs text-slate-400">{stage}</p>
                  </div>
                  <span className="w-fit rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-300">{value}</span>
                </div>
                <p className="mt-3 text-xs text-slate-300">{note}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [mobileMenu, setMobileMenu] = useState(false);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f3f6fb] text-textLight dark:bg-bgDark dark:text-textDark">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute left-[-10rem] top-[-5rem] h-96 w-96 rounded-full bg-sky-300/20 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute right-[-8rem] top-[6rem] h-[28rem] w-[28rem] rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
        <div className="absolute bottom-[-8rem] left-1/3 h-80 w-80 rounded-full bg-primary/15 blur-3xl dark:bg-primary/10" />
      </div>

      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl dark:border-borderDark/80 dark:bg-bgDark/80">
        <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between px-4 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg shadow-slate-900/10 dark:bg-white dark:text-slate-950 sm:h-11 sm:w-11">
              <Building2 className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 sm:text-xs sm:tracking-[0.28em]">BIM Prospect</span>
              <span className="block truncate text-xs font-semibold text-slate-950 dark:text-white sm:text-sm">Sistema para prospeccao B2B</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 text-sm text-slate-600 dark:text-slate-300 md:flex">
            <a href="#beneficios" className="transition hover:text-slate-950 dark:hover:text-white">
              Beneficios
            </a>
            <a href="#como-funciona" className="transition hover:text-slate-950 dark:hover:text-white">
              Como funciona
            </a>
            <a href="#depoimentos" className="transition hover:text-slate-950 dark:hover:text-white">
              Prova social
            </a>
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <ThemeToggle />
            <Link to="/login">
              <Button variant="ghost" className="rounded-full px-5">
                Entrar
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="cta" className="rounded-full px-5">
                Quero vender melhor
              </Button>
            </Link>
          </div>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/90 text-slate-700 shadow-sm dark:border-borderDark dark:bg-cardDark md:hidden"
            onClick={() => setMobileMenu(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 pb-20 pt-6 lg:px-8 lg:pb-24 lg:pt-10">
        <section className="relative overflow-hidden rounded-[1.75rem] border border-white/80 bg-[linear-gradient(135deg,rgba(255,255,255,0.98),rgba(242,248,255,0.92))] px-4 py-6 shadow-[0_28px_90px_-42px_rgba(15,23,42,0.32)] dark:border-white/10 dark:bg-[linear-gradient(135deg,rgba(2,6,23,0.94),rgba(15,23,42,0.88))] sm:px-6 sm:py-8 lg:rounded-[2rem] lg:px-10 lg:py-12">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.14),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.14),transparent_30%)]" />

          <div className="relative grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr]">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
              <h1 className="max-w-3xl text-[2rem] font-black leading-[1.12] tracking-tight text-slate-950 dark:text-white sm:text-4xl lg:text-[4.1rem] lg:leading-[1.06]">
                Pare de perder vendas por falta de apresentacao, clareza e processo comercial.
              </h1>

              <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 dark:text-slate-300 sm:mt-6 sm:text-lg sm:leading-8">
                O BIM Prospect ajuda sua empresa a captar, organizar e converter leads B2B com mais autoridade visual, mais controle operacional e mais confianca na hora de vender.
              </p>

              <div className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button variant="cta" className="h-12 w-full rounded-full px-6 sm:w-auto" iconLeft={<ArrowRight className="h-4 w-4" />}>
                    Solicitar demonstracao
                  </Button>
                </Link>
                <a href="#beneficios" className="w-full sm:w-auto">
                  <Button variant="outline" className="h-12 w-full rounded-full border-slate-300 bg-white/80 px-6 dark:bg-white/5 sm:w-auto">
                    Entender beneficios
                  </Button>
                </a>
              </div>

              <div className="mt-8 space-y-3">
                {promises.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                      <CheckCircle2 className="h-4 w-4" />
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {stats.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.12 + index * 0.07 }}
                    className="rounded-2xl border border-slate-200/80 bg-white/85 p-4 shadow-sm dark:border-white/10 dark:bg-white/5"
                  >
                    <p className="text-2xl font-black text-slate-950 dark:text-white">{item.value}</p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">{item.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, delay: 0.12 }}>
              <ProductShowcase />
            </motion.div>
          </div>
        </section>

        <section className="mt-8 rounded-[1.5rem] border border-slate-200/80 bg-white/80 px-4 py-4 shadow-[0_22px_60px_-45px_rgba(15,23,42,0.4)] dark:border-white/10 dark:bg-white/5 sm:rounded-[1.75rem] sm:px-6 sm:py-5">
          <div className="flex flex-col gap-3 text-center lg:flex-row lg:items-center lg:justify-between lg:text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.3em]">Posicionamento forte para produto B2B</p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-slate-600 dark:text-slate-300 lg:justify-end">
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 dark:border-white/10 dark:bg-white/5">Mais confianca</span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 dark:border-white/10 dark:bg-white/5">Mais clareza</span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 dark:border-white/10 dark:bg-white/5">Mais conversao</span>
            </div>
          </div>
        </section>

        <section className="mt-16">
          <div className="max-w-3xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.28em]">O problema</p>
            <h2 className="mt-5 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl sm:leading-[1.18] lg:mt-6 lg:text-4xl">
              Quando a pagina nao vende sozinha, o cliente chega desconfiado e a conversa fica mais dificil.
            </h2>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {painPoints.map((item) => (
              <Card key={item.title} className="rounded-[1.75rem] border-slate-200/80 bg-white/90 p-7 shadow-[0_24px_70px_-45px_rgba(15,23,42,0.38)] dark:border-white/10 dark:bg-slate-950/65">
                <p className="text-lg font-bold text-slate-950 dark:text-white">{item.title}</p>
                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{item.text}</p>
              </Card>
            ))}
          </div>
        </section>

        <section id="beneficios" className="mt-16 grid gap-4 lg:grid-cols-[0.92fr_1.08fr]">
          <Card className="rounded-[1.75rem] border-slate-200/80 bg-slate-950 p-6 text-white shadow-[0_30px_80px_-40px_rgba(2,6,23,0.85)] dark:border-white/10 sm:rounded-[2rem] sm:p-8">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 sm:text-xs sm:tracking-[0.28em]">A solucao</p>
            <h2 className="mt-5 text-2xl font-black tracking-tight sm:text-3xl sm:leading-[1.18]">Uma apresentacao comercial que valoriza o sistema e mostra ao cliente uma solucao pronta para crescer.</h2>
            <p className="mt-5 text-base leading-7 text-slate-300">
              A apresentacao foi reorganizada para gerar percepcao de valor nos primeiros segundos, comunicar melhor os beneficios e preparar o visitante para a acao.
            </p>

            <div className="mt-8 space-y-4">
              {[
                'Headline forte com promessa clara de negocio',
                'Seccoes que conduzem o visitante em ordem de venda',
                'Visual premium com mais autoridade e menos cara de template'
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 text-sm text-slate-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {solutions.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.08 * index }}
                >
                  <Card className="h-full rounded-[1.75rem] border-slate-200/80 bg-white/90 p-6 shadow-[0_24px_70px_-48px_rgba(15,23,42,0.4)] dark:border-white/10 dark:bg-slate-950/65">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white dark:bg-white dark:text-slate-950">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{item.text}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section id="como-funciona" className="mt-16 rounded-[1.75rem] border border-slate-200/80 bg-white/90 px-4 py-6 shadow-[0_26px_80px_-48px_rgba(15,23,42,0.35)] dark:border-white/10 dark:bg-slate-950/70 sm:px-6 sm:py-8 lg:rounded-[2rem] lg:px-10 lg:py-10">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.28em]">Como funciona</p>
              <h2 className="mt-5 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl sm:leading-[1.18]">A pagina conduz, o produto sustenta e o comercial fecha.</h2>
            </div>
            <p className="max-w-xl text-sm leading-7 text-slate-600 dark:text-slate-300">
              A nova estrutura foi desenhada para fazer o visitante entender rapidamente o valor, sentir confianca e seguir para o proximo passo com menos resistencia.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              ['01', 'Chama atencao', 'O hero apresenta a promessa principal com clareza, visual forte e CTA direto.'],
              ['02', 'Construi desejo', 'As seccoes mostram dores, beneficios e ganho percebido para aumentar interesse.'],
              ['03', 'Leva para acao', 'A prova social e o fechamento reforcam seguranca para o visitante converter.']
            ].map(([step, title, text]) => (
              <Card key={step} className="rounded-[1.75rem] border-slate-200/80 bg-slate-50/80 p-6 shadow-none dark:border-white/10 dark:bg-white/5">
                <span className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-600 dark:text-emerald-300">{step}</span>
                <h3 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">{title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{text}</p>
              </Card>
            ))}
          </div>
        </section>

        <section id="depoimentos" className="mt-16 grid gap-4 lg:grid-cols-[0.92fr_1.08fr]">
          <Card className="rounded-[1.75rem] border-slate-200/80 bg-white/90 p-6 shadow-[0_24px_70px_-46px_rgba(15,23,42,0.35)] dark:border-white/10 dark:bg-slate-950/70 sm:rounded-[2rem] sm:p-8">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.28em]">Prova social</p>
            <h2 className="mt-5 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl sm:leading-[1.18]">Uma landing melhor apresentada aumenta confianca antes mesmo do contato comercial.</h2>
            <p className="mt-5 text-base leading-7 text-slate-600 dark:text-slate-300">
              Mais do que beleza, a pagina agora entrega narrativa, autoridade e organizacao visual para apoiar a venda.
            </p>

            <div className="mt-8 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
                  <p className="text-2xl font-black text-slate-950 dark:text-white">Mais autoridade</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">A pagina transmite produto serio e mais confiavel</p>
                </div>
                <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-slate-900">
                  <p className="text-2xl font-black text-slate-950 dark:text-white">Mais conversao</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">O visitante entende melhor o valor e avanca</p>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid gap-4">
            {testimonials.map((item) => (
              <Card key={item.name} className="rounded-[1.75rem] border-slate-200/80 bg-white/90 p-7 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.38)] dark:border-white/10 dark:bg-slate-950/70">
                <p className="text-lg leading-8 text-slate-600 dark:text-slate-300">"{item.text}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-sm font-bold text-white dark:bg-white dark:text-slate-950">
                    {item.name
                      .split(' ')
                      .map((part) => part[0])
                      .join('')}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-950 dark:text-white">{item.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{item.role}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-16 grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
          <Card className="rounded-[1.75rem] border-slate-200/80 bg-slate-950 p-6 text-white shadow-[0_28px_80px_-42px_rgba(2,6,23,0.8)] dark:border-white/10 sm:rounded-[2rem] sm:p-8">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 sm:text-xs sm:tracking-[0.28em]">FAQ</p>
            <h2 className="mt-5 text-2xl font-black tracking-tight sm:text-3xl sm:leading-[1.18]">Perguntas que travam a decisao do cliente precisam ser respondidas na propria pagina.</h2>
            <p className="mt-5 text-base leading-7 text-slate-300">
              Uma landing de venda nao deve deixar o visitante confuso. Ela precisa antecipar duvidas e reduzir friccao.
            </p>
          </Card>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <Card key={item.question} className="rounded-[1.5rem] border-slate-200/80 bg-white/90 p-6 shadow-[0_18px_60px_-46px_rgba(15,23,42,0.35)] dark:border-white/10 dark:bg-slate-950/70">
                <p className="text-lg font-bold text-slate-950 dark:text-white">{item.question}</p>
                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{item.answer}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-[1.75rem] border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-emerald-50/90 px-4 py-6 shadow-[0_28px_80px_-44px_rgba(15,23,42,0.32)] dark:border-white/10 dark:from-slate-950 dark:via-slate-950 dark:to-emerald-950/40 sm:px-6 sm:py-8 lg:rounded-[2rem] lg:px-10 lg:py-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 sm:text-xs sm:tracking-[0.28em]">Fechamento</p>
              <h2 className="mt-5 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl sm:leading-[1.18]">Se a sua pagina precisa vender melhor, ela precisa parecer pronta para fechar negocio.</h2>
              <p className="mt-5 text-base leading-7 text-slate-600 dark:text-slate-300">
                Agora a landing fala com mais clareza, se apresenta com mais elegancia e conduz o visitante de forma muito mais comercial.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/register" className="w-full sm:w-auto">
                <Button variant="cta" className="h-12 w-full rounded-full px-6 sm:w-auto" iconLeft={<ArrowRight className="h-4 w-4" />}>
                  Quero demonstracao
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <Button variant="primary" className="h-12 w-full rounded-full px-6 sm:w-auto">
                  Entrar na plataforma
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200/80 py-8 text-center text-sm text-slate-500 dark:border-borderDark dark:text-slate-400">
        BIM Prospect © {new Date().getFullYear()} · Prospeccao B2B com mais apresentacao, organizacao e conversao
      </footer>

      <Presence>
        {mobileMenu ? (
          <Modal open={mobileMenu} onClose={() => setMobileMenu(false)}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-950 dark:text-white">Menu</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Navegue pela apresentacao</p>
              </div>
              <button
                type="button"
                onClick={() => setMobileMenu(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-borderLight dark:border-borderDark"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-2">
              <a href="#beneficios" className="block rounded-xl px-3 py-3 text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setMobileMenu(false)}>
                Beneficios
              </a>
              <a href="#como-funciona" className="block rounded-xl px-3 py-3 text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setMobileMenu(false)}>
                Como funciona
              </a>
              <a href="#depoimentos" className="block rounded-xl px-3 py-3 text-sm hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setMobileMenu(false)}>
                Prova social
              </a>
            </div>

            <div className="mt-5 space-y-3">
              <ThemeToggle />
              <Link to="/login" onClick={() => setMobileMenu(false)}>
                <Button variant="primary" className="rounded-full" fullWidth>
                  Entrar
                </Button>
              </Link>
              <Link to="/register" onClick={() => setMobileMenu(false)}>
                <Button variant="cta" className="rounded-full" fullWidth>
                  Quero vender melhor
                </Button>
              </Link>
            </div>
          </Modal>
        ) : null}
      </Presence>
    </div>
  );
}
